import { lazy } from 'react'
import type { ComponentType, ReactNode } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { I18nProvider, useI18n } from './i18n/context.js'
import { SessionProvider, useSession } from './auth/session.js'
import { SiteProvider } from './workspace/site.js'
import { AppShell } from './shell/AppShell.js'
import { SnackbarProvider } from './ui/overlays.js'
import { PageSkeleton } from './ui/primitives.js'
import { paths } from './routes.js'
import { LoginScreen } from './screens/LoginScreen.js'
import { HomeScreen } from './screens/HomeScreen.js'
import { SiteOverviewScreen } from './screens/SiteOverviewScreen.js'
import { ContentListScreen } from './screens/ContentListScreen.js'
import { ContentEditScreen } from './screens/ContentEditScreen.js'
import { NewContentScreen } from './screens/NewContentScreen.js'
import { ContentResolver, NotFoundScreen, SiteGate } from './screens/Resolvers.js'

/**
 * A screen in its own chunk, loaded the first time its address is opened.
 * The first bundle stays what every session needs (shell, home, contents and
 * the editor); the rest — site structure, media, people, operator — comes
 * when asked for. Screens keep their named exports: this adapts them.
 */
function lazyScreen<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }))
}

const ContentHistoryScreen = lazyScreen(() => import('./screens/ContentHistoryScreen.js'), 'ContentHistoryScreen')
const ContentPageScreen = lazyScreen(() => import('./screens/ContentPageScreen.js'), 'ContentPageScreen')
const ReleaseNotesScreen = lazyScreen(() => import('./screens/ReleaseNotesScreen.js'), 'ReleaseNotesScreen')
const SiteSettingsScreen = lazyScreen(() => import('./screens/SiteSettingsScreen.js'), 'SiteSettingsScreen')
const SitePublishingScreen = lazyScreen(() => import('./screens/SitePublishingScreen.js'), 'SitePublishingScreen')
const SiteMenuScreen = lazyScreen(() => import('./screens/SiteMenuScreen.js'), 'SiteMenuScreen')
const SiteLayoutsScreen = lazyScreen(() => import('./screens/SiteLayoutsScreen.js'), 'SiteLayoutsScreen')
const SiteThemeScreen = lazyScreen(() => import('./screens/SiteThemeScreen.js'), 'SiteThemeScreen')
const SectionLibraryScreen = lazyScreen(() => import('./screens/SectionLibraryScreen.js'), 'SectionLibraryScreen')
const NewSiteScreen = lazyScreen(() => import('./screens/NewSiteScreen.js'), 'NewSiteScreen')
const SitePageListScreen = lazyScreen(() => import('./screens/SitePageListScreen.js'), 'SitePageListScreen')
const SitePageDetailScreen = lazyScreen(() => import('./screens/SitePageDetailScreen.js'), 'SitePageDetailScreen')
const SitePageHistoryScreen = lazyScreen(() => import('./screens/SitePageHistoryScreen.js'), 'SitePageHistoryScreen')
const SitePageComposerScreen = lazyScreen(() => import('./screens/SitePagesScreen.js'), 'SitePageComposerScreen')
const SiteListingsScreen = lazyScreen(() => import('./screens/SiteListingsScreen.js'), 'SiteListingsScreen')
const SitePublicationsScreen = lazyScreen(() => import('./screens/SitePublicationsScreen.js'), 'SitePublicationsScreen')
const CompanyKnowledgeScreen = lazyScreen(() => import('./screens/CompanyKnowledgeScreen.js'), 'CompanyKnowledgeScreen')
const PeopleScreen = lazyScreen(() => import('./screens/PeopleScreen.js'), 'PeopleScreen')
const InvitationsScreen = lazyScreen(() => import('./screens/InvitationsScreen.js'), 'InvitationsScreen')
const AccessPreviewScreen = lazyScreen(() => import('./screens/AccessPreviewScreen.js'), 'AccessPreviewScreen')
const ApiKeysScreen = lazyScreen(() => import('./screens/ApiKeysScreen.js'), 'ApiKeysScreen')
const OperatorScreen = lazyScreen(() => import('./screens/OperatorScreen.js'), 'OperatorScreen')
const NewTenantScreen = lazyScreen(() => import('./screens/NewTenantScreen.js'), 'NewTenantScreen')
const MediaLibraryScreen = lazyScreen(() => import('./screens/MediaLibraryScreen.js'), 'MediaLibraryScreen')
const ScheduleScreen = lazyScreen(() => import('./screens/ScheduleScreen.js'), 'ScheduleScreen')
const RedirectsScreen = lazyScreen(() => import('./screens/RedirectsScreen.js'), 'RedirectsScreen')
const EditorialPlanScreen = lazyScreen(() => import('./screens/EditorialPlanScreen.js'), 'EditorialPlanScreen')
const EditorialPlanItemScreen = lazyScreen(() => import('./screens/EditorialPlanItemScreen.js'), 'EditorialPlanItemScreen')

/**
 * Access is single sign-on, so "is there a session?" is a question for the
 * API, not for this browser (document/10 RF-60). While it is being asked the
 * screen says so; only a clear "no" sends a person to the login — carrying
 * the page they asked for, so a shared link survives the round trip.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useSession()
  const { t } = useI18n()
  const location = useLocation()

  if (state.status === 'loading') return <div className="app-loading"><PageSkeleton label={t('auth.checking')}/></div>
  if (state.status !== 'signed-in') return <Navigate to={paths.login(`${location.pathname}${location.search}`)} replace/>
  return <>{children}</>
}

function Workspace() {
  return <RequireAuth>
    <SiteProvider>
      <AppShell><Outlet/></AppShell>
    </SiteProvider>
  </RequireAuth>
}

/**
 * `BrowserRouter` with clean addresses (`/s/<site>/contenuti/<id>`): every
 * screen and every selection is a real URL. Cloudflare Pages answers unknown
 * paths with `index.html` through `public/_redirects`, after serving the
 * files that exist (`/version.json`, `/.well-known/…`). Addresses from before
 * 0.8.0 (`/#/contents/<id>`) are rewritten in `main.tsx` before this renders.
 */
export function App() {
  return (
    <I18nProvider>
      <SnackbarProvider>
        <SessionProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/accesso" element={<LoginScreen/>}/>
              <Route path="/login" element={<Navigate to="/accesso" replace/>}/>
              <Route element={<Workspace/>}>
                <Route index element={<HomeScreen/>}/>
                <Route path="s/:siteId" element={<SiteGate/>}>
                  <Route index element={<SiteOverviewScreen/>}/>
                  <Route path="contenuti" element={<ContentListScreen/>}/>
                  <Route path="contenuti/nuovo" element={<NewContentScreen/>}/>
                  <Route path="contenuti/:id" element={<ContentEditScreen/>}/>
                  <Route path="contenuti/:id/pagina" element={<ContentPageScreen/>}/>
                  <Route path="contenuti/:id/storia" element={<ContentHistoryScreen/>}/>
                  <Route path="media" element={<MediaLibraryScreen/>}/>
                  <Route path="media/:assetId" element={<MediaLibraryScreen/>}/>
                  <Route path="calendario" element={<ScheduleScreen/>}/>
                  <Route path="piano" element={<EditorialPlanScreen/>}/>
                  <Route path="piano/:itemId" element={<EditorialPlanItemScreen/>}/>
                  <Route path="redirect" element={<RedirectsScreen/>}/>
                  <Route path="pagine" element={<SitePageListScreen/>}/>
                  <Route path="pagine/:pageId" element={<SitePageDetailScreen/>}/>
                  <Route path="pagine/:pageId/sezioni" element={<SitePageComposerScreen/>}/>
                  <Route path="pagine/:pageId/storia" element={<SitePageHistoryScreen/>}/>
                  <Route path="menu" element={<SiteMenuScreen/>}/>
                  <Route path="menu/:menuKey" element={<SiteMenuScreen/>}/>
                  <Route path="layout" element={<SiteLayoutsScreen/>}/>
                  <Route path="layout/:layoutKey" element={<SiteLayoutsScreen/>}/>
                  <Route path="elenchi" element={<SiteListingsScreen/>}/>
                  <Route path="elenchi/:listingKey" element={<SiteListingsScreen/>}/>
                  <Route path="tema" element={<SiteThemeScreen/>}/>
                  <Route path="sezioni" element={<SectionLibraryScreen/>}/>
                  <Route path="sezioni/:sectionKey" element={<SectionLibraryScreen/>}/>
                  <Route path="pubblicazioni" element={<SitePublicationsScreen/>}/>
                  <Route path="impostazioni" element={<SiteSettingsScreen/>}/>
                  <Route path="impostazioni/pubblicazione" element={<SitePublishingScreen/>}/>
                  <Route path="*" element={<NotFoundScreen/>}/>
                </Route>
                <Route path="contenuti/:id" element={<ContentResolver/>}/>
                <Route path="contenuti/:id/:view" element={<ContentResolver/>}/>
                <Route path="persone" element={<PeopleScreen/>}/>
                <Route path="persone/inviti" element={<InvitationsScreen/>}/>
                <Route path="persone/accesso" element={<AccessPreviewScreen/>}/>
                <Route path="persone/:personId" element={<PeopleScreen/>}/>
                <Route path="chiavi-api" element={<ApiKeysScreen/>}/>
                <Route path="operatore" element={<OperatorScreen/>}/>
                <Route path="operatore/nuovo-cliente" element={<NewTenantScreen/>}/>
                <Route path="conoscenza" element={<CompanyKnowledgeScreen/>}/>
                <Route path="siti/nuovo" element={<NewSiteScreen/>}/>
                <Route path="note-di-rilascio" element={<ReleaseNotesScreen/>}/>
                <Route path="*" element={<NotFoundScreen/>}/>
              </Route>
            </Routes>
          </BrowserRouter>
        </SessionProvider>
      </SnackbarProvider>
    </I18nProvider>
  )
}
